import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import supabaseService from '../services/supabaseService';
import { FiMapPin, FiHash, FiArrowLeft, FiUser } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

const ClientPublicProfileScreen = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAppBanner, setShowAppBanner] = useState(false);

  const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);
  const playStoreLink = 'https://play.google.com/store/apps/details?id=com.fares.khidma';
  const appDeepLink = `https://khidmadz.shop/client-public/${clientId}`;

  useEffect(() => {
    loadClientProfile();
    const choice = localStorage.getItem('app_choice_made');
    if (isMobile && !choice) {
      setShowAppBanner(true);
    }
  }, [clientId, isMobile]);

  const loadClientProfile = async () => {
    try {
      const clientData = await supabaseService.getClientById(clientId);
      if (clientData) {
        setClient(clientData);
      } else {
        toast.error('لم يتم العثور على العميل');
      }
    } catch (error) {
      console.error('Error loading client profile:', error);
      toast.error('فشل تحميل الملف الشخصي');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenApp = () => {
    if (isAndroid) {
      const intentUrl = `intent://khidmadz.shop/client-public/${clientId}#Intent;scheme=https;package=com.fares.khidma;end`;
      window.location.href = intentUrl;
      setTimeout(() => {
        window.location.href = playStoreLink;
      }, 2000);
    } else {
      window.location.href = appDeepLink;
      setTimeout(() => {
        window.location.href = playStoreLink;
      }, 2000);
    }
  };

  const handleCloseBanner = () => {
    localStorage.setItem('app_choice_made', 'true');
    setShowAppBanner(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <FiUser className="mx-auto text-6xl text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">العميل غير موجود</h2>
          <p className="text-gray-600 mb-6">لم يتم العثور على بيانات العميل</p>
          <button
            onClick={() => navigate('/')}
            className="bg-gray-900 text-white py-2 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors"
          >
            العودة للرئيسية
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">

      {/* App Banner - موبايل فقط */}
      {showAppBanner && isMobile && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={handleCloseBanner}
        >
          <div
            className="w-full bg-white rounded-t-3xl p-6 shadow-2xl"
            style={{ maxWidth: 480 }}
            onClick={e => e.stopPropagation()}
          >
            {/* Handle */}
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" />

            {/* Logo + Title */}
            <div className="flex items-center gap-4 mb-6" dir="rtl">
              <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <span className="text-white font-black text-2xl">K</span>
              </div>
              <div>
                <h2 className="text-xl font-black text-gray-900">تطبيق Khidma</h2>
                <p className="text-gray-500 text-sm mt-0.5">
                  افتح في التطبيق للحصول على تجربة أفضل
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="space-y-3">
              {/* فتح في التطبيق */}
              <button
                onClick={handleOpenApp}
                className="flex items-center justify-center gap-2 w-full bg-black text-white text-center py-4 rounded-2xl font-bold text-base"
              >
                <span>📱</span>
                <span>فتح في التطبيق</span>
              </button>

              {/* تحميل التطبيق */}
              <a
                href={playStoreLink}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-gray-100 text-gray-800 text-center py-4 rounded-2xl font-bold text-base no-underline"
              >
                <span>⬇️</span>
                <span>تحميل التطبيق</span>
              </a>

              {/* الاستمرار في الموقع */}
              <button
                onClick={handleCloseBanner}
                className="w-full text-gray-400 text-center py-3 text-sm"
              >
                المتابعة في الموقع
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <FiArrowLeft />
            <span>رجوع</span>
          </button>
          <h1 className="text-2xl font-bold text-gray-900">الملف الشخصي</h1>
          <div className="w-16"></div>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header Background */}
          <div className="bg-gradient-to-r from-gray-900 to-black h-32"></div>

          {/* Profile Content */}
          <div className="px-6 pb-6">
            {/* Avatar */}
            <div className="flex justify-center -mt-16 mb-4">
              <img
                src={client.avatarUrl || 'https://via.placeholder.com/150'}
                alt={client.fullName}
                className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
              />
            </div>

            {/* Name */}
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
              {client.fullName || 'اسم العميل'}
            </h2>

            {/* Client ID Badge */}
            <div className="flex justify-center mb-6">
              <div className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full">
                <FiHash className="text-gray-600" />
                <span className="font-mono text-gray-900 font-medium">
                  {client.clientId || 'N/A'}
                </span>
              </div>
            </div>

            {/* Info Grid */}
            <div className="space-y-3">
              {client.address && (
                <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
                  <div className="bg-blue-100 rounded-full p-2">
                    <FiMapPin className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">العنوان</p>
                    <p className="font-medium text-gray-900">{client.address}</p>
                  </div>
                </div>
              )}

              {client.createdAt && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">عضو منذ</p>
                  <p className="font-medium text-gray-900">
                    {new Date(client.createdAt).toLocaleDateString('ar-SA', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              )}
            </div>

            {/* Note */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 text-center">
                هذا ملف شخصي عام. البريد الإلكتروني ورقم الهاتف غير معروضين للحفاظ على الخصوصية.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            مدعوم من <span className="font-bold text-gray-900">خدمة</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClientPublicProfileScreen;